/**
 * 评估器量化指标（evaluator-metrics-spec）——纯结构化 metrics 读取（前端侧）。
 *
 * 评估器把量化指标以结构化字段 `evaluate_info.metrics` 上报：
 *   { "<key>": { value: number, label: 中文名, unit?: string, higher_is_worse?: boolean } }
 *
 * 本模块**只支持结构化 metrics**，不再解析 msg、不维护前置注册表——「有哪些指标、
 * 叫什么、方向语义」全部来自评估器上报数据。指标清单由后端接口
 * `GET /daily/compare/evaluator-metrics` 动态返回（见 apis/simulation.ts）。
 */
import type { EvaluatorItem } from '@apis/simulation';

/** 后端 /daily/compare/evaluator-metrics 返回的单条指标定义（评估器自描述）。 */
export interface EvaluatorMetricDef {
  key: string;
  label: string;
  unit?: string;
  /** 方向语义：true=增大是退化(红) / false=增大是改善(绿) / null=未知(中性蓝)。 */
  higherIsWorse?: boolean | null;
}

// 评测器名归一到 snake_case，与后端 _canonical_evaluator_name 同口径（用于跨命名风格匹配）。
const RE_CAMEL_BOUNDARY = /([a-z0-9])([A-Z])/g;
const RE_CAMEL_ACRONYM = /([A-Z])([A-Z][a-z])/g;

export function canonicalEvaluatorName(name?: string | null): string {
  if (!name) return '';
  return name
    .trim()
    .replace(RE_CAMEL_ACRONYM, '$1_$2')
    .replace(RE_CAMEL_BOUNDARY, '$1_$2')
    .toLowerCase();
}

function toNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * 从一条评估项里取指定指标的数值（`metrics[key].value`）。
 * `metrics[key]` 允许是 {value,...} 对象或直接标量。缺失/非数字返回 null。
 */
export function extractMetric(
  item: EvaluatorItem | undefined | null,
  metricKey: string,
): number | null {
  if (!item) return null;
  const metricsObj = (item as unknown as Record<string, unknown>).metrics;
  if (!metricsObj || typeof metricsObj !== 'object') return null;
  const entry = (metricsObj as Record<string, unknown>)[metricKey];
  if (entry && typeof entry === 'object') {
    return toNumber((entry as Record<string, unknown>).value);
  }
  return toNumber(entry);
}

/**
 * 从一个 task 的评估项列表里，取指定评估器该指标的数值。
 * 找不到评估器行 / 无该指标返回 null。
 */
export function extractMetricFromItems(
  items: EvaluatorItem[] | undefined,
  evaluatorName: string,
  metricKey: string,
): number | null {
  if (!items || items.length === 0) return null;
  const target = canonicalEvaluatorName(evaluatorName);
  const row = items.find((it) => canonicalEvaluatorName(it.evaluatorName) === target);
  return extractMetric(row ?? null, metricKey);
}
